// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek / Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartEvent,
  ChartOptions,
  Point,
  PointElement,
  LegendItem,
  LineController,
  LineElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  Filler,
  TimeUnit,
} from "chart.js";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";
import ResizeObserver from "resize-observer-polyfill";

import { TimePeriod, DashboardPanel } from "types";
import { isSet } from "utils";
import {
  COLOR_SELECT,
  getTrendIoGraphViewMode,
  getGraphMinUnits,
  hexToRgba,
  getGraphBoundaryDates,
  getColor,
  getPanelSelection,
  getIoType,
  getSiteSelection,
} from "guiutils";
import { ActionType, IAction, raiseAction, IActionTypeZoom, IActionTypeSelectIos } from "actions";
import { IInput, IOutput } from "parameter";
import { IState } from "states/states";
import { GraphViewMode } from "states/cachedstates";
import { IDropdownSelectOption } from "./DropdownSelect";
import { IIoTrendCount } from "states/trendingstates";

import { getAllAvailableIos, getSelectedIos } from "view/page/IoTrend";
import { ReactComponent as IconZoomFit } from "static/iconZoomFit.svg";
import cssContent from "content.scss";

const NR_TLS_HIDE_ZERO = 10;

// register used chartjs elements
ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip,
  zoomPlugin,
  Filler,
);

interface IIoGraphCountExtProps {
  panel?: DashboardPanel;
  dashboardIndex?: number;
}

type IIoGraphCountProps = IIoGraphCountExtProps & {
  isLoading: boolean;
  queryMoreData: boolean;
  lastUpdate: number | undefined;
  selectedInputs: number[] | undefined;
  selectedOutputs: number[] | undefined;
  ioTrendCount: IIoTrendCount;
  startDate: number | undefined;
  endDate: number | undefined;
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
  // deviceTimestamp: number | undefined;
  timezoneOffset: number | undefined;
  graphViewMode: GraphViewMode;
  input: IInput[] | undefined;
  output: IOutput[] | undefined;
  selectedIoType: "analog" | "digital";
};

const mapStateToProps = (state: IState, ownProps: IIoGraphCountExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    ownProps.panel ?? DashboardPanel.OUTPUTS,
    ownProps.dashboardIndex,
  );
  const selectedIoType = getIoType(state);
  const siteSelection = getSiteSelection(state);

  const props: IIoGraphCountProps = {
    ...ownProps,
    isLoading: state.scheduler.isIoTrendLoading,
    queryMoreData: state.trending.queryMoreData,
    lastUpdate: state.scheduler.ioTrendLoading.lastData, // to ensure screen update
    selectedInputs: siteSelection?.inputs,
    selectedOutputs: siteSelection?.outputs,
    ioTrendCount: state.trending.ioTrendCount,
    input: state.parameter?.input,
    output: state.parameter?.output,
    startDate: state.gui.ioPeriod.startDate,
    endDate: state.gui.ioPeriod.endDate,
    xZoom: state.gui.ioPeriod.timePeriod,
    minX: state.gui.ioPeriod.zoomMin,
    maxX: state.gui.ioPeriod.zoomMax,
    // we only need the device timestamp if graph start date is not set
    // @@@Marc: do not use the deviceTimestamp for now, since this timestamp is changeing
    // with the live-data every 5 Seconds, which would cause a refresh of the Graphs
    // deviceTimestamp:
    //   state.gui.ioPeriod.startDate === undefined ? state.data.timestamp : undefined,
    timezoneOffset: state.site.tzOffset,
    graphViewMode:
      getTrendIoGraphViewMode(
        selection,
        selectedIoType,
        Object.keys(state.trending.ioTrendFull).length > 0,
        true,
      ) ?? GraphViewMode.BAR,
    selectedIoType,
  };
  return props;
};

interface IIoGraphCountDispatch {
  onZooming: (minX?: number, maxX?: number) => void;
  onDropdownIOSelectionChanged: (
    type: "digital" | "analog",
    inputIds: number[],
    outputIds: number[],
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoGraphCountDispatch = {
    onZooming: (minX?: number, maxX?: number) => {
      const actionPayload: IActionTypeZoom = {
        identifier: "ioPeriod",
        minX,
        maxX,
      };
      dispatch(raiseAction(ActionType.ACT_ZOOM_GRAPH, actionPayload));
    },
    onDropdownIOSelectionChanged: (type, inputIds, outputIds) => {
      const payload: IActionTypeSelectIos = { type, inputIds, outputIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_IOS, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  resetZoom: {
    id: "reset zoom",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
  labelOn: {
    id: "On",
  },
  labelOff: {
    id: "Off",
  },
  loading: {
    id: "Data loading...",
  },
});

// State is never set so we use the 'undefined' type.
class IoGraphCountComponent extends React.Component<
  IIoGraphCountProps & IIoGraphCountDispatch & WrappedComponentProps
> {
  private resizeObserver: ResizeObserver | undefined;
  private chartRef = React.createRef<Chart>();
  private readonly compRef = React.createRef<HTMLDivElement>();
  private startTime?: number;
  private endTime?: number;
  private data: [number[][]] = [[]]; // array of [timestamp, y] values for each graph
  private oldHeight = 0;
  private height = 0;
  private maxValue = 0;
  private singleMaxValue = 0;
  private minUnits: TimeUnit = "month";
  private selectedOptions: IDropdownSelectOption[] = [];
  private zoomLock = false;
  private panLock = false;

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((elem: ResizeObserverEntry[]) => {
      if (this.chartRef.current && elem[0].contentRect.height) {
        // yes, this is odd
        // on screen build-up on the dashboard this graphs parent div gets resized twice
        // however, the graph just resizes the first one. We need to force screen update
        // by unmount/remount which we achieve by changing the divs key attribute
        // since the first resize is OK, we use oldHeight and a 2-stage FIFO
        this.oldHeight = this.height;
        this.height = elem[0].contentRect.height;
        //console.log("forcing update", this.oldHeight, this.height);
      }
    });
    if (this.compRef.current) this.resizeObserver.observe(this.compRef.current);
    else console.warn("did not register graph size observer");
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }

  public render() {
    const allIos = getAllAvailableIos(
      [this.props.input ?? [], this.props.output ?? []],
      this.props.selectedIoType,
    );
    const selectedOptions = getSelectedIos(
      allIos,
      this.props.selectedInputs,
      this.props.selectedOutputs,
      true,
    );
    this.selectedOptions = [...selectedOptions[0], ...selectedOptions[1]];

    // make some general calculations
    this.preProcessData();

    // check for existing data
    let anyDataPoints = 0;
    this.data.forEach((aDataSet) => {
      anyDataPoints = anyDataPoints + aDataSet.length;
    });

    if (this.props.isLoading || this.props.queryMoreData) {
      return (
        <div id="iograph" className="graph" ref={this.compRef}>
          <div className="loading-spinner-box">
            <div id="loading-spinner"></div>
            <div id="loading-text">
              <FormattedMessage id={messages.loading.id} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div id="iograph" className="graph" ref={this.compRef} key={this.oldHeight}>
        {isSet(this.props.minX) || isSet(this.props.maxX) ? (
          <div
            className="graph-zoom"
            onClick={this.resetZoom.bind(this)}
            title={this.props.intl.formatMessage(messages.resetZoom)}
          >
            <IconZoomFit />
          </div>
        ) : (
          <></>
        )}
        {(this.props.isLoading || this.props.queryMoreData) && <div id="loading-spinner" />}
        <Chart
          ref={(c) => {
            if (isSet(c)) this.chartRef = c as any;
          }}
          type={this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar"}
          data={this.createGraphData()}
          options={this.createGraphOptions()}
        />
      </div>
    );
  }

  private preProcessData() {
    // some general data pre-processing
    this.data = [[]];
    const oldMaxValue = this.maxValue;
    const oldSingleMaxValue = this.singleMaxValue;
    this.maxValue = 0;
    this.singleMaxValue = 0;

    this.selectedOptions.forEach((selectedIo, dataIndex) => {
      const sourceIndex = selectedIo?.source === "input" ? 0 : 1;
      let curMax = 0;

      const graphDates = getGraphBoundaryDates(
        this.props.startDate,
        this.props.endDate,
        this.props.timezoneOffset,
        this.props.xZoom,
      );
      this.startTime = this.props.minX ? this.props.minX / 1000 : graphDates.startDate;
      this.endTime = this.props.maxX ? this.props.maxX / 1000 : graphDates.endDate;
      this.minUnits = getGraphMinUnits(this.endTime! - this.startTime!);

      // prepare data over the whole graph!! (otherwise the lines won't get drawn)
      const startTimeData = graphDates.startDate!;
      const endTimeData = graphDates.endDate!;

      const dataTimestamps =
        selectedIo !== undefined
          ? Object.keys(this.props.ioTrendCount)
              .map((item) => parseInt(item))
              .filter((curItem) => curItem >= startTimeData && curItem < endTimeData)
          : [];

      this.data[dataIndex] = [];

      // not all timestamps may have a value for this IO associated, therefore we cannot use map()
      dataTimestamps.forEach((curTimestamp) => {
        const curCount =
          this.props.ioTrendCount[curTimestamp]?.[sourceIndex]?.[selectedIo.index] ?? 0;
        // derive maximum
        if (curMax < curCount) {
          curMax = curCount;
        }
        if (!isNaN(curCount)) {
          this.data[dataIndex].push([curTimestamp, curCount]);
        } else {
          this.data[dataIndex].push([curTimestamp, 0]);
        }
      });

      if (selectedIo.visible === true) {
        this.maxValue += curMax;
        if (this.singleMaxValue < curMax) {
          this.singleMaxValue = curMax;
        }
      }
    });

    if (this.maxValue === 0) {
      this.maxValue = oldMaxValue;
    }
    if (this.singleMaxValue === 0) {
      this.singleMaxValue = oldSingleMaxValue;
    }

    // put 5% overhead to not put the max on the top-point of the graph view
    this.maxValue = Math.round(this.maxValue * 1.05);
    this.singleMaxValue = Math.round(this.singleMaxValue * 1.05);
  }

  private createGraphOptions() {
    const isLegendTop = this.selectedOptions.length <= NR_TLS_HIDE_ZERO;
    const options: ChartOptions<"line" | "bar"> = {
      scales: {
        x: {
          type: "time",
          time: {
            //unit: this.useBarChart ? "hour" : "day",
            minUnit: this.minUnits,
            // this.props.xZoom === TimePeriod.YEAR
            //   ? "month"
            //   : this.props.xZoom >= TimePeriod.WEEK
            //   ? "day"
            //   : this.props.xZoom === TimePeriod.DAY
            //   ? "hour"
            //   : "minute",
            displayFormats: {
              minute: this.props.intl.formatMessage(messages.graphFormatAxisMinute),
              hour: this.props.intl.formatMessage(messages.graphFormatAxisHour),
              day: this.props.intl.formatMessage(messages.graphFormatAxisDay),
            },
          },
          min: this.startTime ? this.startTime * 1000 : undefined,
          // set max date one month earlier at year view?
          max: this.endTime ? this.endTime * 1000 : undefined,
          stacked: true, // only relevant for bar chart
        },
        y: {
          type: "linear",
          max: this.props.graphViewMode === GraphViewMode.BAR ? this.maxValue : this.singleMaxValue,
          ticks: {
            stepSize:
              this.props.graphViewMode === GraphViewMode.BAR
                ? this.maxValue % 500
                : this.singleMaxValue % 500,
          },
          min: undefined,
          beginAtZero: true,
          display: true,
          stacked: this.props.graphViewMode === GraphViewMode.BAR ? true : false,
        },
      },
      animation: {
        duration: 0,
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      elements: {
        point: {
          radius: 2,
        },
      },
      datasets: {
        line: {
          tension: 0.2, // line smoothness, default 0; use less here to get realistic SoC
        },
      },
      plugins: {
        legend: {
          display: window.innerWidth > parseInt(cssContent.GRID_BREAKPOINT_MD) || isLegendTop,
          position: isLegendTop ? "top" : "right",
          onClick: (evt, legendItem, legend) => this.legendItemClick(evt, legendItem, legend),
        },
        tooltip: {
          enabled: true,
          mode: "point", // use "index" to show all data items on same x value
          position: "nearest", // show tooltip on average value, not at mouse cursor
          intersect: true,
          callbacks: {
            title: (tooltipItems: TooltipItem<"line">[]) => {
              const curItem = tooltipItems[0];
              const date = new Date(curItem.parsed.x);
              const localeOptions: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "short",
                day: this.props.xZoom !== TimePeriod.YEAR ? "numeric" : undefined,
                hour:
                  this.props.xZoom !== TimePeriod.YEAR && this.props.xZoom !== TimePeriod.MONTH
                    ? "numeric"
                    : undefined,
                minute:
                  this.props.xZoom !== TimePeriod.YEAR && this.props.xZoom !== TimePeriod.MONTH
                    ? "numeric"
                    : undefined,
              };
              return date.toLocaleString(this.props.intl.locale, localeOptions);
            },
            label: (tooltipItem: TooltipItem<"bar" | "line">) => {
              // do we face a background fill data set?
              if (tooltipItem.dataset.label === undefined) {
                return undefined as unknown as string; // we need to disable the wrong type check here
              }
              const value: string = tooltipItem.formattedValue;

              return ` ${tooltipItem.dataset.label}: ${value}`;
            },
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
            modifierKey: "shift",
            onPanStart: (context) => this.panStartCallback(context.chart, context.point),
            onPanComplete: () => this.panCompleteCallback(),
            onPanRejected: () => this.panCompleteCallback(),
          },
          zoom: {
            mode: "x",
            wheel: {
              enabled: true,
            },
            drag: {
              enabled: true,
              borderColor: hexToRgba(COLOR_SELECT, 0.7),
              backgroundColor: hexToRgba(COLOR_SELECT, 0.5),
            },
            pinch: {
              enabled: true,
            },
            onZoom: (context) => this.zoomCallback(context.chart),
            onZoomStart: (context) => this.zoomCheck(context.chart, context.event, context.point),
            onZoomComplete: (context) => this.zoomCompleteCallback(context.chart),
            onZoomRejected: (context) => this.zoomCompleteCallback(context.chart),
          },
          limits: {
            x: {
              min: "original",
              max: "original",
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
    };

    return options;
  }

  private createGraphData() {
    const parameter: (IInput | IOutput | undefined)[] = this.selectedOptions.map((curItem) =>
      curItem.source === "input"
        ? this.props.input?.[curItem.index]
        : this.props.output?.[curItem.index],
    );

    const data: ChartData<"bar" | "line", ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    if (!this.props.isLoading && !this.props.queryMoreData) {
      this.data.forEach((dataset, anIndex) => {
        const points: ScatterDataPoint[] =
          this.selectedOptions[anIndex]?.visible === true
            ? dataset.map((curItem, ind) => {
                return {
                  x: curItem[0] * 1000, // timstamp
                  y: curItem[1],
                };
              })
            : [];
        const color = getColor(this.selectedOptions[anIndex]?.index ?? 0);
        data.datasets.push({
          label: parameter !== undefined ? parameter[anIndex]?.name : "",
          data: points,
          type: this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar",
          segment: {},
          fill: {},
          hidden: this.selectedOptions[anIndex]?.visible === false,
          borderColor: color,
          backgroundColor: color,
        });
      });
    }
    return data;
  }

  private zoomCallback(chart: ChartJS) {
    const axis = chart.scales.x;
    this.props.onZooming(axis.min, axis.max);
  }

  private resetZoom() {
    this.props.onZooming();
  }

  private zoomCheck(chart: ChartJS, event: Event, point: Point) {
    this.panLock =
      !this.zoomLock &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;
    return this.panLock;
  }

  private zoomCompleteCallback(chart: ChartJS) {
    this.panLock = false;
  }

  private panStartCallback(chart: ChartJS, point: Point) {
    this.zoomLock =
      !this.panLock &&
      this.props.minX !== undefined &&
      this.props.maxX !== undefined &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;

    return this.zoomLock;
  }
  private panCompleteCallback() {
    this.zoomLock = false;
  }

  private legendItemClick(evt: ChartEvent, legendItem: LegendItem, legend: any) {
    const index = legendItem.datasetIndex;
    const ci = legend.chart;
    let newSelectedIos: IDropdownSelectOption[] = [];

    if (index === undefined) {
      return;
    }

    if ((evt.native as PointerEvent).shiftKey) {
      // check if any curve is already hidden...
      const filtered = this.selectedOptions.filter((io) => io.visible === false);
      const anyHidden = filtered ? filtered.length > 0 : 0;

      // check if the clicked curve is hidden AND if any other curve is also hidden
      if (legendItem.hidden === false && anyHidden) {
        // if yes: show all again
        newSelectedIos =
          this.selectedOptions.map((anItem) => {
            // update entry
            anItem.visible = true;
            return anItem;
          }) ?? [];
      } else {
        // otherwise: hide all except current one.... (leave var empty)
        newSelectedIos =
          this.selectedOptions
            .filter((anItem, anIndex) => {
              return anIndex === index;
            })
            .map((anItem) => {
              // update entry
              anItem.visible = Boolean(legendItem.hidden);
              return anItem;
            }) ?? [];
      }
    } else {
      newSelectedIos = (
        this.selectedOptions.map((anItem, anIndex) => {
          if (anIndex === index) {
            // update entry
            anItem.visible = Boolean(legendItem.hidden);
          }
          return anItem;
        }) ?? []
      ).filter((item) => item.visible);
    }

    this.props.onDropdownIOSelectionChanged(
      this.props.selectedIoType,
      newSelectedIos.filter((curItem) => curItem.source === "input").map((curItem) => curItem.id),
      newSelectedIos.filter((curItem) => curItem.source === "output").map((curItem) => curItem.id),
    );
  }
}

const IoGraphCount = connect(mapStateToProps, mapDispatchToProps)(IoGraphCountComponent);

export default injectIntl(IoGraphCount);
